<template>
  <div class="relatorios-page">

    <!-- Page header -->
    <div class="page-header">
      <div class="page-header-inner">
        <div>
          <div class="page-title">Relatórios</div>
          <div class="page-subtitle">
            {{ isAdmin ? 'Visão geral do desempenho da plataforma' : 'O seu desempenho pessoal' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="content-wrapper">
      <div class="row q-col-gutter-lg q-mb-lg">
        <div v-for="n in 3" :key="n" class="col-12 col-sm-4">
          <div class="skeleton-card">
            <div class="q-pa-lg">
              <q-skeleton type="text" width="40%" class="q-mb-md" />
              <q-skeleton type="text" width="50%" height="48px" class="q-mb-sm" />
              <q-skeleton type="text" width="80%" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div v-else class="content-wrapper">

      <!-- ── ADMIN VIEW ── -->
      <template v-if="isAdmin">

        <div class="tabs-wrapper">
          <q-tabs
            v-model="activeTab"
            align="left"
            active-color="primary"
            indicator-color="primary"
            no-caps
            dense
            class="report-tabs"
          >
            <q-tab name="resumo"    icon="dashboard"    label="Resumo" />
            <q-tab name="imoveis"   icon="home"         label="Imóveis" />
            <q-tab name="top"       icon="star"         label="Top Imóveis" />
            <q-tab name="agentes"   icon="people"       label="Agentes" />
            <q-tab name="precos"    icon="attach_money" label="Preços" />
            <q-tab name="atividade" icon="timeline"     label="Atividade" />
            <q-tab name="publicacoes" icon="view_list" label="Publicações" />
            <q-tab name="contactos" icon="contacts" label="Contactos" />
          </q-tabs>
        </div>

        <q-tab-panels v-model="activeTab" animated keep-alive class="panels-bg">

          <!-- ── RESUMO ── -->
          <q-tab-panel name="resumo" class="q-pa-none">
            <div class="row q-col-gutter-lg">
              <div class="col-12 col-sm-6 col-md-4">
                <div class="stat-card">
                  <div class="stat-icon-wrap icon-navy">
                    <q-icon name="home" size="22px" />
                  </div>
                  <div class="stat-label">Imóveis</div>
                  <div class="stat-value">{{ summary?.properties?.total ?? '—' }}</div>
                  <div class="stat-pills">
                    <span class="pill pill-green">{{ summary?.properties?.active }} ativos</span>
                    <span class="pill pill-red">{{ summary?.properties?.deleted }} eliminados</span>
                  </div>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-md-4">
                <div class="stat-card">
                  <div class="stat-icon-wrap icon-gold">
                    <q-icon name="people" size="22px" />
                  </div>
                  <div class="stat-label">Utilizadores</div>
                  <div class="stat-value">{{ summary?.users?.total ?? '—' }}</div>
                  <div class="stat-pills">
                    <span class="pill pill-green">{{ summary?.users?.active }} ativos</span>
                    <span class="pill pill-grey">{{ summary?.users?.agents }} agentes</span>
                    <span class="pill pill-grey">{{ summary?.users?.admins }} admins</span>
                  </div>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-md-4">
                <div class="stat-card">
                  <div class="stat-icon-wrap icon-blue">
                    <q-icon name="visibility" size="22px" />
                  </div>
                  <div class="stat-label">Engagement</div>
                  <div class="stat-value">{{ summary?.engagement?.total_clicks ?? '—' }}</div>
                  <div class="stat-pills">
                    <span class="pill pill-grey">{{ summary?.engagement?.total_clicks }} cliques</span>
                    <span class="pill pill-grey">{{ summary?.engagement?.total_images }} imagens</span>
                  </div>
                </div>
              </div>
            </div>
          </q-tab-panel>

          <!-- ── IMÓVEIS ── -->
          <q-tab-panel name="imoveis" class="q-pa-none">
            <div class="row q-col-gutter-lg">
              <div class="col-12 col-md-6">
                <div class="content-card">
                  <div class="card-title">Por Tipo</div>
                  <div v-for="item in propertiesByType" :key="item.type" class="dist-item">
                    <div class="dist-row">
                      <span>{{ item.type }}</span>
                      <span class="dist-count">{{ item.total }} <em class="dist-pct">({{ item.percentage }}%)</em></span>
                    </div>
                    <q-linear-progress :value="item.percentage / 100" color="primary" rounded size="6px" />
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="content-card">
                  <div class="card-title">Por Objetivo</div>
                  <div v-for="item in propertiesByObjective" :key="item.objective" class="dist-item">
                    <div class="dist-row">
                      <span>{{ item.objective }}</span>
                      <span class="dist-count">{{ item.total }} <em class="dist-pct">({{ item.percentage }}%)</em></span>
                    </div>
                    <q-linear-progress :value="item.percentage / 100" color="secondary" rounded size="6px" />
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="content-card">
                  <div class="card-title">Por Condição</div>
                  <div v-for="item in propertiesByCondition" :key="item.condition" class="dist-item">
                    <div class="dist-row">
                      <span>{{ item.condition }}</span>
                      <span class="dist-count">{{ item.total }} <em class="dist-pct">({{ item.percentage }}%)</em></span>
                    </div>
                    <q-linear-progress :value="item.percentage / 100" color="positive" rounded size="6px" />
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="content-card">
                  <div class="card-title">Por Localização</div>
                  <q-table
                    :rows="propertiesByLocation"
                    :columns="locationColumns"
                    row-key="location"
                    flat hide-bottom
                    :rows-per-page-options="[0]"
                    class="report-table"
                  />
                </div>
              </div>
            </div>
          </q-tab-panel>

          <!-- ── TOP IMÓVEIS ── -->
          <q-tab-panel name="top" class="q-pa-none">
            <div class="content-card">
              <div class="card-title-row">
                <span class="card-title">Top Imóveis por Cliques</span>
                <q-select
                  v-model="topLimit"
                  :options="[5, 10, 20, 50]"
                  label="Limite"
                  dense outlined
                  style="width:90px"
                  @update:model-value="loadTopProperties"
                />
              </div>
              <q-table
                :rows="topProperties"
                :columns="topColumns"
                row-key="slug"
                flat
                :rows-per-page-options="[0]"
                class="report-table"
              />
            </div>
          </q-tab-panel>

          <!-- ── AGENTES ── -->
          <q-tab-panel name="agentes" class="q-pa-none">
            <div class="content-card">
              <div class="card-title">Performance dos Agentes</div>
              <q-table
                :rows="agentPerformance"
                :columns="agentColumns"
                row-key="id"
                flat
                :rows-per-page-options="[10, 20, 0]"
                class="report-table"
              />
            </div>
          </q-tab-panel>

          <!-- ── PREÇOS ── -->
          <q-tab-panel name="precos" class="q-pa-none">
            <div class="row q-col-gutter-lg">
              <div class="col-12 col-md-6">
                <div class="content-card">
                  <div class="card-title">Por Objetivo</div>
                  <q-table
                    :rows="priceAnalytics?.by_objective ?? []"
                    :columns="priceColumns('objective')"
                    row-key="objective"
                    flat hide-bottom
                    :rows-per-page-options="[0]"
                    class="report-table"
                  />
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="content-card">
                  <div class="card-title">Por Tipo de Imóvel</div>
                  <q-table
                    :rows="priceAnalytics?.by_property_type ?? []"
                    :columns="priceColumns('type')"
                    row-key="type"
                    flat hide-bottom
                    :rows-per-page-options="[0]"
                    class="report-table"
                  />
                </div>
              </div>
            </div>
          </q-tab-panel>

          <!-- ── ATIVIDADE ── -->
          <q-tab-panel name="atividade" class="q-pa-none">
            <div class="content-card">
              <div class="card-title-row">
                <span class="card-title">Atividade Mensal</span>
                <q-select
                  v-model="months"
                  :options="[6, 12, 24]"
                  label="Meses"
                  dense outlined
                  style="width:90px"
                  @update:model-value="loadMonthlyActivity"
                />
              </div>
              <q-table
                :rows="mergedMonthly"
                :columns="monthlyColumns"
                row-key="month"
                flat
                :rows-per-page-options="[0]"
                class="report-table"
              />
            </div>
          </q-tab-panel>

          <q-tab-panel name="publicacoes" class="q-pa-none">
            <ListingReports />
          </q-tab-panel>

          <q-tab-panel name="contactos" class="q-pa-none">
            <ContactsReport />
          </q-tab-panel>

        </q-tab-panels>
      </template>

      <!-- ── AGENT VIEW ── -->
      <template v-else>
        <div class="row q-col-gutter-lg q-mb-lg">
          <div class="col-12 col-sm-4">
            <div class="stat-card">
              <div class="stat-icon-wrap icon-navy">
                <q-icon name="home" size="22px" />
              </div>
              <div class="stat-label">Imóveis Ativos</div>
              <div class="stat-value">{{ mySummary?.total_active_properties ?? '—' }}</div>
            </div>
          </div>
          <div class="col-12 col-sm-4">
            <div class="stat-card">
              <div class="stat-icon-wrap icon-red">
                <q-icon name="delete" size="22px" />
              </div>
              <div class="stat-label">Eliminados</div>
              <div class="stat-value">{{ mySummary?.total_deleted_properties ?? '—' }}</div>
            </div>
          </div>
          <div class="col-12 col-sm-4">
            <div class="stat-card">
              <div class="stat-icon-wrap icon-gold">
                <q-icon name="visibility" size="22px" />
              </div>
              <div class="stat-label">Cliques Totais</div>
              <div class="stat-value">{{ mySummary?.total_clicks ?? '—' }}</div>
            </div>
          </div>
        </div>

        <div class="row q-col-gutter-lg q-mb-lg">
          <div class="col-12 col-md-6">
            <div class="content-card">
              <div class="card-title">Por Objetivo</div>
              <div v-for="item in mySummary?.by_objective ?? []" :key="item.objective" class="dist-item">
                <div class="dist-row">
                  <span>{{ item.objective }}</span>
                  <span class="dist-count">{{ item.total }}</span>
                </div>
                <q-linear-progress
                  :value="item.total / (mySummary?.total_active_properties || 1)"
                  color="secondary" rounded size="6px"
                />
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6">
            <div class="content-card">
              <div class="card-title">Por Tipo</div>
              <div v-for="item in mySummary?.by_type ?? []" :key="item.type" class="dist-item">
                <div class="dist-row">
                  <span>{{ item.type }}</span>
                  <span class="dist-count">{{ item.total }}</span>
                </div>
                <q-linear-progress
                  :value="item.total / (mySummary?.total_active_properties || 1)"
                  color="primary" rounded size="6px"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="content-card">
          <div class="card-title">Top 5 Imóveis</div>
          <q-table
            :rows="mySummary?.top_5_properties ?? []"
            :columns="myTopColumns"
            row-key="slug"
            flat hide-bottom
            :rows-per-page-options="[0]"
            class="report-table"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import store from '@/vuex/store';
import ListingReports from './ListingReports.vue';
import ContactsReport from './ContactsReport.vue';

const loading = ref(true);
const activeTab = ref('resumo');
const topLimit = ref(10);
const months = ref(12);

const isAdmin = computed(() => store.state.auth.user?.role === 1);

const summary           = computed(() => store.state.reports.summary);
const propertiesByType      = computed(() => store.state.reports.propertiesByType);
const propertiesByObjective = computed(() => store.state.reports.propertiesByObjective);
const propertiesByCondition = computed(() => store.state.reports.propertiesByCondition);
const propertiesByLocation  = computed(() => store.state.reports.propertiesByLocation);
const topProperties     = computed(() => store.state.reports.topProperties);
const priceAnalytics    = computed(() => store.state.reports.priceAnalytics);
const agentPerformance  = computed(() => store.state.reports.agentPerformance);
const monthlyActivity   = computed(() => store.state.reports.monthlyActivity);
const mySummary         = computed(() => store.state.reports.mySummary);

const mergedMonthly = computed(() => {
  const props = monthlyActivity.value?.properties_per_month ?? [];
  const users = monthlyActivity.value?.users_per_month ?? [];
  const map = {};
  props.forEach(r => { map[r.month] = { month: r.month, properties: r.total, users: 0 }; });
  users.forEach(r => { if (map[r.month]) map[r.month].users = r.total; else map[r.month] = { month: r.month, properties: 0, users: r.total }; });
  return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
});

const fmt = (val, currency) => {
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency, minimumFractionDigits: 0 }).format(val);
  } catch { return val; }
};

// ─── Table column definitions ───────────────────────────────────────────────

const locationColumns = [
  { name: 'location', label: 'Localização', field: 'location', align: 'left', sortable: true },
  { name: 'total',    label: 'Total',       field: 'total',    align: 'right', sortable: true },
];

const topColumns = [
  { name: 'title',    label: 'Título',    field: 'title',    align: 'left',  sortable: true },
  { name: 'location', label: 'Local',     field: 'location', align: 'left',  sortable: true },
  { name: 'type',     label: 'Tipo',      field: 'property_type', align: 'left' },
  { name: 'objective',label: 'Objetivo',  field: 'objective', align: 'left' },
  { name: 'clicks',   label: 'Cliques',   field: 'clicks',   align: 'right', sortable: true },
  { name: 'agent',    label: 'Agente',    field: row => row.agent?.name ?? '—', align: 'left' },
  { name: 'price',    label: 'Preço',     field: row => fmt(row.price, row.currency), align: 'right' },
];

const agentColumns = [
  { name: 'name',     label: 'Nome',       field: 'name',           align: 'left',  sortable: true },
  { name: 'email',    label: 'Email',      field: 'email',          align: 'left' },
  { name: 'props',    label: 'Imóveis',    field: 'property_count', align: 'right', sortable: true },
  { name: 'clicks',   label: 'Cliques',    field: 'total_clicks',   align: 'right', sortable: true },
  { name: 'active',   label: 'Estado',     field: row => row.active ? 'Ativo' : 'Inativo', align: 'center' },
  { name: 'since',    label: 'Membro desde', field: row => row.member_since?.slice(0, 10) ?? '—', align: 'left' },
];

const priceColumns = (keyField) => [
  { name: keyField,  label: keyField === 'objective' ? 'Objetivo' : 'Tipo', field: keyField, align: 'left', sortable: true },
  { name: 'currency',label: 'Moeda',   field: 'currency', align: 'center' },
  { name: 'total',   label: 'Total',   field: 'total',    align: 'right',  sortable: true },
  { name: 'avg',     label: 'Média',   field: row => fmt(row.avg_price, row.currency),  align: 'right' },
  { name: 'min',     label: 'Mínimo',  field: row => fmt(row.min_price, row.currency),  align: 'right' },
  { name: 'max',     label: 'Máximo',  field: row => fmt(row.max_price, row.currency),  align: 'right' },
];

const monthlyColumns = [
  { name: 'month',      label: 'Mês',     field: 'month',      align: 'left',  sortable: true },
  { name: 'properties', label: 'Imóveis', field: 'properties', align: 'right', sortable: true },
  { name: 'users',      label: 'Utilizadores', field: 'users', align: 'right', sortable: true },
];

const myTopColumns = [
  { name: 'title',    label: 'Título',   field: 'title',    align: 'left',  sortable: true },
  { name: 'location', label: 'Local',    field: 'location', align: 'left' },
  { name: 'clicks',   label: 'Cliques',  field: 'clicks',   align: 'right', sortable: true },
  { name: 'price',    label: 'Preço',    field: row => fmt(row.price, row.currency), align: 'right' },
];

// ─── Data loading ────────────────────────────────────────────────────────────

async function loadTopProperties() {
  await store.dispatch('reports/getTopProperties', topLimit.value);
}

async function loadMonthlyActivity() {
  await store.dispatch('reports/getMonthlyActivity', months.value);
}

onMounted(async () => {
  try {
    if (isAdmin.value) {
      await Promise.all([
        store.dispatch('reports/getSummary'),
        store.dispatch('reports/getPropertiesByType'),
        store.dispatch('reports/getPropertiesByObjective'),
        store.dispatch('reports/getPropertiesByCondition'),
        store.dispatch('reports/getPropertiesByLocation'),
        store.dispatch('reports/getTopProperties', topLimit.value),
        store.dispatch('reports/getPriceAnalytics'),
        store.dispatch('reports/getAgentPerformance'),
        store.dispatch('reports/getMonthlyActivity', months.value),
      ]);
    } else {
      await store.dispatch('reports/getMySummary');
    }
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
/* ── Page ──────────────────────────────────────────────── */
.relatorios-page {
  background: #f4f6fb;
  min-height: 100vh;
}

/* ── Header ────────────────────────────────────────────── */
.page-header {
  background: #0d1b2a;
  padding: 20px 24px;
}
.page-header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
}
.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
}
.page-subtitle {
  font-size: 12px;
  color: rgba(212, 175, 55, 0.75);
  letter-spacing: 0.4px;
  margin-top: 2px;
}

/* ── Content wrapper ───────────────────────────────────── */
.content-wrapper {
  max-width: 1400px;
  margin: 28px auto;
  padding: 0 20px 48px;
}

/* ── Skeleton ──────────────────────────────────────────── */
.skeleton-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e8ecf4;
}

/* ── Tabs ──────────────────────────────────────────────── */
.tabs-wrapper {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e8ecf4;
  margin-bottom: 24px;
  overflow: hidden;
}
.report-tabs {
  padding: 0 8px;
}
:deep(.report-tabs .q-tab) {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding: 12px 16px;
  color: #64748b;
}
:deep(.report-tabs .q-tab--active) {
  color: #0d1b2a !important;
}
:deep(.report-tabs .q-tab__indicator) {
  background: #d4af37;
  height: 3px;
  border-radius: 2px 2px 0 0;
}

/* ── Tab panels ────────────────────────────────────────── */
.panels-bg {
  background: transparent !important;
}
:deep(.panels-bg .q-tab-panel) {
  background: transparent;
  padding: 0;
}

/* ── Stat card ─────────────────────────────────────────── */
.stat-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8ecf4;
  padding: 24px;
  transition: box-shadow 0.25s ease;
}
.stat-card:hover {
  box-shadow: 0 8px 24px rgba(13, 27, 42, 0.08);
}

.stat-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: #fff;
}
.icon-navy { background: #0d1b2a; }
.icon-gold  { background: #d4af37; color: #0d1b2a !important; }
.icon-blue  { background: rgba(37, 99, 235, 0.88); }
.icon-red   { background: rgba(220, 38, 38, 0.88); }

.stat-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 6px;
}
.stat-value {
  font-size: 38px;
  font-weight: 800;
  color: #0d1b2a;
  letter-spacing: -1.5px;
  line-height: 1;
  margin-bottom: 14px;
}
.stat-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
}
.pill-green { background: rgba(22, 163, 74, 0.1);  color: #16a34a; }
.pill-red   { background: rgba(220, 38, 38, 0.1);  color: #dc2626; }
.pill-grey  { background: #f1f5f9; color: #64748b; }

/* ── Content card ──────────────────────────────────────── */
.content-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e8ecf4;
  padding: 24px;
  transition: box-shadow 0.25s ease;
}
.content-card:hover {
  box-shadow: 0 4px 16px rgba(13, 27, 42, 0.06);
}

.card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0d1b2a;
  margin-bottom: 20px;
}
.card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.card-title-row .card-title { margin-bottom: 0; }

/* ── Distribution bars ─────────────────────────────────── */
.dist-item {
  margin-bottom: 16px;
}
.dist-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #475569;
  margin-bottom: 6px;
}
.dist-count {
  font-weight: 700;
  color: #0d1b2a;
}
.dist-pct {
  font-weight: 400;
  font-style: normal;
  color: #94a3b8;
}

/* ── Report table ──────────────────────────────────────── */
:deep(.report-table .q-table__top),
:deep(.report-table .q-table__bottom) {
  border-top: 1px solid #f1f5f9;
  padding: 8px 0;
}
:deep(.report-table thead tr th) {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #94a3b8;
  border-bottom: 1px solid #e8ecf4;
  padding: 10px 12px;
}
:deep(.report-table tbody tr td) {
  font-size: 13px;
  color: #334155;
  padding: 10px 12px;
  border-bottom: 1px solid #f8fafc;
}
:deep(.report-table tbody tr:hover td) {
  background: #f8fafc;
}
</style>
